<template>
  <main class="main">
    <hm-col :grid="false" cols="12" class="btn--scroll__wrapper">
      <hm-button :disabled="firstColumn + visibleColumns >= seasons.length"
                 :title="$t('admin.pageSeasonRateEditor.actionScrollSeasonRight')"
                 class="btn--no-margin btn--icon-only"
                 cols="1 start-12"
                 icon-left="forward-1c"
                 @click="firstColumn++"></hm-button>
      <hm-button :disabled="firstColumn <= 0"
                 :title="$t('admin.pageSeasonRateEditor.actionScrollSeasonLeft')"
                 class="btn--no-margin btn--icon-only"
                 cols="1"
                 icon-left="back-1c"
                 @click="firstColumn--"></hm-button>
    </hm-col>
    <hm-col v-if="loaded" :grid="true" cols="12" class="sticky">
      <portal to="headerTitle">
        {{ ratePlan.name[hotelSettings.details.primaryLanguage] }}
      </portal>
      <template v-for="(season, idx) in seasons">
        <hm-col v-if="idx >= firstColumn && idx < (firstColumn + visibleColumns)"
                :title="season.name[hotelSettings.details.primaryLanguage]"
                :style="{ backgroundColor: `rgba(${toRGB(season.rgbColorCode)}, 0.0)` }"
                :key="season.id"
                :grid="true"
                :cols="`${12/visibleColumns}`"
                class="grid--row-gap">
          <card :title="season.name[hotelSettings.details.primaryLanguage]"
                :style="`--season-color: rgba(${toRGB(season.rgbColorCode)}, 0.6);`"
                :collapsed="!collapsedByRoomType['season']"
                card-type="collapsible"
                cols="12"
                header-style-class="card__header--season-color"
                @collapse="collapsedByRoomType['season'] = !$event">
            <hm-card-inner>
              <hm-col cols="12">
                <span>
                  <template v-for="dateRange in dateColors[season.id]">
                    {{ dateRange.start | toDate }} - {{ dateRange.end | toDate }}<br/>
                  </template>
                </span>
              </hm-col>
            </hm-card-inner>
          </card>
        </hm-col>
      </template>
    </hm-col>
    <hm-col v-if="loaded" :grid="true" cols="12">
      <template v-for="(season, idx) in seasons">
        <hm-col v-if="idx >= firstColumn && idx < (firstColumn + visibleColumns)"
                :style="{ backgroundColor: `rgba(${toRGB(season.rgbColorCode)}, 0.0)` }"
                :key="season.id"
                :grid="true"
                :cols="`${12/visibleColumns}`"
                class="grid--row-gap">
          <template v-for="roomType in hotelSettings.roomTypes.all()" v-if="!roomType.deleted && isEmpty(roomType.parentRoomTypeId) && !isEmpty(ratePlan.applicableRoomTypes) && ratePlan.applicableRoomTypes.includes(roomType.id)">
            <room-type-season-rate-editor v-model="data.roomTypeRatePlanSeasonDetails[`${season.id}::${roomType.id}`]"
                                          :disabled="ratePlan.deleted"
                                          :season="season"
                                          :room-type="roomType"
                                          :rate-plan="ratePlan"
                                          :key="`${roomType.id}_rates`"
                                          :expanded="collapsedByRoomType[roomType.id]"
                                          @collapse="collapsedByRoomType[roomType.id] = !collapsedByRoomType[roomType.id]"/>
          </template>
          <season-restriction-editor v-model="data.ratePlanSeasonDetail[season.id]"
                                     :disabled="ratePlan.deleted"
                                     :season="season"
                                     :rate-plan="ratePlan"
                                     :key="`${season.id}_restrictions`" />
        </hm-col>
      </template>
    </hm-col>
    <hm-col :grid="false" cols="12" class="btn--scroll__wrapper">
      <hm-button :disabled="firstColumn + visibleColumns >= seasons.length"
                 :title="$t('admin.pageSeasonRateEditor.actionScrollSeasonRight')"
                 class="btn--no-margin btn--icon-only"
                 cols="1 start-12"
                 icon-left="forward-1c"
                 @click="firstColumn++"></hm-button>
      <hm-button :disabled="firstColumn <= 0"
                 :title="$t('admin.pageSeasonRateEditor.actionScrollSeasonLeft')"
                 class="btn--no-margin btn--icon-only"
                 cols="1"
                 icon-left="back-1c"
                 @click="firstColumn--"></hm-button>
    </hm-col>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import fecha from 'fecha';
import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import vue2Dropzone from 'vue2-dropzone';
import draggable from 'vuedraggable';
import { cloneDeep, groupBy, isEmpty, isNil, sortBy, keyBy, keys, mapValues, map, first, last } from 'lodash';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmInputMoney from '../../lib/inputs/HmInputMoney';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import SeasonCalendar from '../../lib/calendar/SeasonCalendar';
import BaseHmInput from '../../lib/inputs/BaseHmInput';
import HmInputToggle from '../../lib/inputs/HmInputToggle';
import HmInputRate from '../../lib/inputs/HmInputRate';
import RoomTypeSeasonRateEditor from '../../lib/seasonRateEditor/RoomTypeSeasonRateEditor';
import HmInputNumber from '../../lib/inputs/HmInputNumber';
import HmInputCheckbox from '../../lib/inputs/HmInputCheckbox';

export default {
  name: 'PageSeasonRateEditor',
  components: { HmInputCheckbox, HmInputToggle, HmInputNumber, RoomTypeSeasonRateEditor, HmInputRate, HmCardInner },
  mixins: [HotelmeisterMixin, SaveWatchMixin],

  autosave: [
  ],

  data() {
    return {
      data: null,
      ratePlan: null,
      collapsedByRoomType: {
        season: false,
      },
      dateColors: {},
      dataLoaded: 0,
      firstColumn: 0,
    };
  },

  computed: {
    ratePlanId: function () {
      return this.$route.params.ratePlanId;
    },
    loaded: function () {
      return this.dataLoaded >= 2;
    },
    seasons: function () {
      if (isNil(this.ratePlan)) return [];
      return this.hotelSettings.seasons.all().filter(season => !season.deleted && season.ratePlanType === this.ratePlan.ratePlanType);
    },
    visibleColumns: function () {
      switch (this.$store.getters['layout/activeBreakpoint']) {
        case 'hd':
          return 4;
        case 'desktop':
          return 3;
        case 'tablet':
          return 2;
        case 'mobile':
        default:
          return 1;
      }
    },
  },

  watch: {
  },

  created: function () {
    this.ratePlan = this.findHotelSettingById('ratePlans', this.ratePlanId);
    if (isNil(this.ratePlan)) {
      this.$router.push({ name: 'ratePlans' });
    }
    if (this.ratePlan.rateEntryMode === 'Daily') {
      this.$router.push({ name: 'dailyRateEditor' });
    }

    this.hotelSettings.roomTypes.filter(it => !it.deleted).map(it => it.id).forEach((it) => { this.$set(this.collapsedByRoomType, it, false); });
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    loadData() {
      this.axios.get(this.serviceUrls.seasonRate.get(this, this.ratePlanId))
        .then((response) => {
          this.data = response.data.data;
          this.data.ratePlanSeasonDetail = keyBy(this.data.ratePlanSeasonDetail, it => it.season);
          this.data.roomTypeRatePlanSeasonDetails = keyBy(this.data.roomTypeRatePlanSeasonDetails, it => `${it.season}::${it.roomType}`);
          this.dataLoaded++;
        });

      const minDate = this.$moment().add(-1, 'days').format('YYYY-MM-DD');
      const maxDate = this.$moment().add(this.hotelSettings.details.futureDates, 'days').format('YYYY-MM-DD');

      this.axios.get(this.serviceUrls.seasonDate.list(this, minDate, maxDate))
        .then((response) => {
          let dateColors = response.data.data;
          const key = this.ratePlan.ratePlanType === 'DayRate' ? 'season' : 'packageSeason';
          dateColors = groupBy(dateColors, key);
          delete dateColors.null;

          Object.keys(dateColors).forEach((it) => {
            let dates = map(dateColors[it], 'date');

            dates = sortBy(dates);
            dates = map(dates, date => fecha.parse(date, 'YYYYMMDD'));

            let dateGroups = [];
            let activeDateGroup = [];
            let lastDate = null;
            dates.forEach((date) => {
              if (!isNil(lastDate)) {
                const testDate = new Date(lastDate);
                testDate.setDate(lastDate.getDate() + 1);
                if (fecha.format(testDate, 'YYYYMMDD') !== fecha.format(date, 'YYYYMMDD')) {
                  dateGroups.push(activeDateGroup);
                  activeDateGroup = [];
                }
              }

              activeDateGroup.push(date);
              lastDate = date;
            });

            if (activeDateGroup.length > 0) {
              dateGroups.push(activeDateGroup);
            }

            dateGroups = dateGroups.map(dateRange => ({
              start: fecha.format(first(dateRange), 'YYYYMMDD'),
              end: fecha.format(last(dateRange), 'YYYYMMDD'),
            }));

            dateColors[it] = dateGroups;
          });

          this.dateColors = dateColors;

          this.dataLoaded++;
        });
    },
    intArray: function (value, offset = 0) {
      return [...Array(value).keys()].map(x => x + offset);
    },
    collapse: function (roomTypeId) {
      this.collapsedByRoomType[roomTypeId] = !this.collapsedByRoomType[roomTypeId];
    },
    toRGB(hexColorCode) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColorCode);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '';
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';

</style>

<style lang="scss">

  .sticky {
    position: sticky!important;
    top: 0;
    z-index: 15;
  }

  .card__header {
    &--season-color {
      background-color: var(--season-color)!important;

      &:before {
        display: none!important;
      }
    }
  }

  .hm-checkbox {
    &__helper-text {
      opacity: 1!important;
      top: 0.3rem!important;
      margin-top: 0.3rem!important;;
      position: relative!important;
      left: 0!important;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      margin-bottom: 1rem!important;
    }
  }

  .btn--scroll__wrapper {
    .btn--icon-only {
      &:nth-child(1) {
        float:left;
        float:right;
        margin-right: 2rem;
      }

      &:nth-child(2) {
        float:right;
        right: 1rem;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageSeasonRateEditor.vue